import { useFormatsStore } from '@/store/use-formats-store';
import { useImagesStore } from '@/store/use-images-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { debounce } from 'lodash-es';
import { watchfaceImageLoading, watchfaceOpening } from './use-watchface-init';
import { onActivated, onDeactivated, onMounted, ref } from 'vue';
import { useRouteState } from './use-route-state';
import { useEmitterListener } from './use-event-listener';
import { EventType } from '@/utils/emitter.utils';
import { watchfaceExporting } from '.';
import { ElectronApi } from '@/services/electron.api';
import {
  convertImageBlobByWorker,
  initWebworkers,
  terminateWebworkers
} from '@/services/compiler-refactor/web-worker-convert-image-blob/indes';
import {
  getCacheKey,
  getIs16bpp,
  imageCahcehIdb,
  needCacheConvertData
} from '@/services/compiler-refactor/pngConvert/utils/image.utls';
import { mainRenderer } from './use-canvas-render';
import { getCachedImageKeysByWorker } from '@/utils/web-worker/cached-image-keys';

// 画布渲染后是否在处理图片中
const cachingAfterRender = ref(false);

const cachingImageData = ref<{
  total: Set<string>;
  cached: Set<string>;
}>({
  total: new Set(),
  cached: new Set()
});

type CacheImageItem = {
  imgPath: string;
  basename: string;
  format: string;
  cacheKey: string;
  arrayBuffer?: ArrayBuffer;
};

const _cachedImageSetInDB = ref<Set<string>>(new Set());

export const useImageBufferCache = (listening = false) => {
  const imageStore = useImagesStore();
  const formateStore = useFormatsStore();
  const watchfaceStore = useWatchfaceStore();
  const { isEditorPage } = useRouteState();

  // 获取可执行的web worker数量
  const getWebWorkerLen = () => {
    const min = 2;
    const max = 8;
    const cups = ElectronApi.os?.cpus().length ?? 2;
    const len = Math.min(max, Math.max(min, cups));
    return len;
  };

  const needCache = (format?: string) => {
    return needCacheConvertData({
      watchfaceType: watchfaceStore.deviceType,
      format
    });
  };

  const cacheWholeImages = async () => {
    if (!needCache()) return;

    // 缓存全部时，需要记录进度
    cachingImageData.value.total = new Set();
    cachingImageData.value.cached = new Set();

    // 如果有任务正在进行，等待完成
    if (cachingAfterRender.value) {
      await new Promise((reslove) => {
        let timer = 0;
        try {
          timer = window.setInterval(() => {
            if (!cachingAfterRender.value) {
              window.clearInterval(timer);
              reslove(undefined);
            }
          });
        } catch (error) {
          window.clearInterval(timer);
          reslove(undefined);
        }
      });
    }
    const cachedKeySet = await _getCachedImageSetInDB();
    const needCacheImages = await _getNeedCacheImages(cachedKeySet, true);

    // 获取当前已缓存的数据
    const cachedImages = imageStore.imgs.filter(
      (item) =>
        !needCacheImages.some((item2) => item2.basename == item.basename)
    );
    cachingImageData.value.total = new Set(
      imageStore.imgs.map((item) => item.basename)
    );
    cachingImageData.value.cached = new Set(
      cachedImages.map((item) => item.basename)
    );
    await _cacheImagesBuffer(needCacheImages);
  };

  const _cacheImagesBuffer = async (images: CacheImageItem[]) => {
    if (!images.length) return;

    try {
      console.time('缓存图片convert');
      const is16bpp = getIs16bpp(watchfaceStore.deviceType);
      const step = getWebWorkerLen();
      let index = 0;
      const promises: Promise<void>[] = [];
      // 初始化web worker
      initWebworkers(step);
      while (index < images.length) {
        if (!isEditorPage.value) break;
        promises.length = 0;
        for (let i = index; i < index + step; i++) {
          if (!isEditorPage.value) break;

          if (i >= images.length) break;
          const webWorkerIndex = i % step;
          const { imgPath, format, cacheKey } = images[i];

          promises.push(
            (async () => {
              try {
                const res = await convertImageBlobByWorker(
                  {
                    imgPath,
                    format,
                    is16bpp,
                    cacheKey
                  },
                  webWorkerIndex
                )
                  .then((r) => r)
                  .catch(() => {
                    // 在这里确保单个图片，出错误了，不影响全局图片
                    return undefined;
                  });
                if (res) {
                  const items = images.filter(
                    (item) => item.cacheKey === res.cacheKey
                  );
                  if (items.length && res.arraybuffer) {
                    items.forEach((item) => {
                      item.arrayBuffer = res.arraybuffer;
                      cachingImageData.value.cached.add(item.basename);
                    });
                  }
                  _cachedImageSetInDB.value.add(res.cacheKey);
                }
              } catch (error) {
                console.log(error);
              }
            })()
          );
        }
        await Promise.all(promises);
        index += step;
      }

      // 等全部都处理完成后再进行统一保存
      for (const item of images) {
        if (item.arrayBuffer) {
          await imageCahcehIdb.putItem(item.cacheKey, item.arrayBuffer);
        }
      }
    } catch (error) {
      console.trace(error);
    } finally {
      console.timeEnd('缓存图片convert');
      // 关闭web worker
      terminateWebworkers();
    }
  };

  const _getNeedCacheImages = async (
    cachedKeySet: Set<string>,
    whole = false
  ) => {
    const is16bpp = getIs16bpp(watchfaceStore.deviceType);
    const needCacheImages: CacheImageItem[] = [];
    const _pushImage = async (item: StoreResourceItem) => {
      const { basename, value, realpath } = item;
      const format = formateStore.format(basename);
      if (!value || !format || !realpath) return;
      if (!needCache(format)) return;

      if (whole || mainRenderer?.runtime?.images?.[basename]) {
        const cacheKey = await getCacheKey({
          imgPath: realpath,
          format,
          is16bpp
        });
        // 不能获取cacheKey，说明图片不存在
        if (!cacheKey) return;
        if (cachedKeySet.has(cacheKey)) return;

        needCacheImages.push({
          imgPath: realpath,
          basename,
          format,
          cacheKey
        });
      }
    };

    for (const item of imageStore.imgs) {
      await _pushImage(item);
    }
    return needCacheImages;
  };

  const _getCachedImageSetInDB = async () => {
    if (!_cachedImageSetInDB.value.size) {
      const cachedKeySet = await getCachedImageKeysByWorker();
      _cachedImageSetInDB.value = cachedKeySet;
    }
    return _cachedImageSetInDB.value;
  };

  const cacheAfterRender = debounce(async () => {
    if (!isEditorPage.value) return;
    if (!needCache()) return;

    try {
      cachingAfterRender.value = true;
      console.time('cacheAfterRender');

      console.time('cacheAfterRender-getCachedKeySetByWorker');
      const cachedKeySet = await _getCachedImageSetInDB();
      console.timeEnd('cacheAfterRender-getCachedKeySetByWorker');

      console.time('cacheAfterRender-getNeedCacheImages');
      const needCacheImages = await _getNeedCacheImages(cachedKeySet, false);
      console.timeEnd('cacheAfterRender-getNeedCacheImages');

      await _cacheImagesBuffer(needCacheImages);
    } catch (error) {
      console.log(error);
    } finally {
      console.timeEnd('cacheAfterRender');
      cachingAfterRender.value = false;
    }
  }, 1000);

  useEmitterListener(EventType['maml:rendered'], (data: { reason: string }) => {
    const { reason } = data ?? {};

    if (!isEditorPage.value) return;
    if (!listening) return;
    if (watchfaceOpening.value || watchfaceImageLoading.value) return;
    if (watchfaceExporting.value) return;
    if (cachingAfterRender.value) return;

    if (
      [
        'theme-changed',
        'config-data-changed',
        'shape-transformed',
        'canvas-scale',
        'keep-data'
      ].includes(reason)
    ) {
      return;
    }
    cacheAfterRender();
  });

  const clear = () => {
    if (!listening) return;
    cachingAfterRender.value = false;
    terminateWebworkers();
    _cachedImageSetInDB.value.clear();
  };

  const initCachedImageSetInDB = async () => {
    if (!needCache()) return;
    const cachedKeySet = await getCachedImageKeysByWorker();
    _cachedImageSetInDB.value = cachedKeySet;
  };

  onMounted(clear);
  onActivated(clear);
  onDeactivated(clear);
  return {
    cacheWholeImages,
    cachingImageData,
    initCachedImageSetInDB
  };
};
